import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, getRememberedEmail } from '@/context/AuthContext';
import client, { API } from '@/lib/api';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, KeyRound, Eye, EyeOff, ChevronDown, Zap } from 'lucide-react';

const DEMO_ACCOUNTS = [
  { label: 'Developer', email: 'servoapex@gmail.com', password: 'servoapex@1989' },
  { label: 'Company Admin', email: 'admin@hibret.com', password: 'Hibretg@1991' },
  { label: 'DER', email: 'der@hibret.com', password: 'Hibretg@2000' },
  { label: 'Owner Operator', email: 'owner@hibret.com', password: 'Hibretg@2001' },
  { label: 'Driver', email: 'driver@hibret.com', password: 'Hibretg@2026' },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(getRememberedEmail());
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [branding, setBranding] = useState(null);

  // Resolve the company's sign-in branding (logo) shortly after the email is entered.
  useEffect(() => {
    const e = (email || '').trim().toLowerCase();
    if (!e || !e.includes('@') || !e.split('@')[1]?.includes('.')) { setBranding(null); return; }
    const id = setTimeout(async () => {
      try {
        const r = await client.get(`/auth/branding?email=${encodeURIComponent(e)}`);
        setBranding(r.data && r.data.has_logo ? r.data : null);
      } catch { setBranding(null); }
    }, 500);
    return () => clearTimeout(id);
  }, [email]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid email or password');
    } finally { setLoading(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // First-party Google OAuth: send the current origin so the backend builds a
    // matching redirect URI for this exact domain (preview / deployed / custom).
    const origin = window.location.origin;
    window.location.href = `${API}/auth/google/login?origin=${encodeURIComponent(origin)}`;
  };

  const microsoft = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const origin = window.location.origin;
    window.location.href = `${API}/auth/microsoft/login?origin=${encodeURIComponent(origin)}`;
  };

  const quickFill = (acc) => { setEmail(acc.email); setPassword(acc.password); setQuickOpen(false); toast.info(`Filled ${acc.label} credentials`); };

  // Demo quick-fill is only shown on non-production origins (local dev / preview).
  const host = (typeof window !== 'undefined' && window.location.hostname) || '';
  const showQuickFill = /localhost|127\.0\.0\.1|\.preview\./i.test(host);

  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-start overflow-y-auto bg-[hsl(var(--foreground))] p-4 sm:justify-center">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(1200px_circle_at_15%_0%,rgba(245,179,1,0.22),transparent_55%),radial-gradient(900px_circle_at_95%_20%,rgba(37,99,235,0.28),transparent_55%)]" />
      <div className="relative z-10 my-auto grid w-full max-w-5xl gap-6 py-6 lg:grid-cols-2">
        <div className="hidden flex-col justify-between rounded-2xl bg-white/5 p-10 text-white lg:flex">
          <Logo light />
          <div>
            <h2 className="font-display text-4xl font-bold leading-tight">DOT & FMCSA compliance,<br /><span className="text-[hsl(var(--gold))]">simplified.</span></h2>
            <p className="mt-4 max-w-sm text-white/70">Manage drivers, fleet and documents with automated expiration alerts and smart compliance gating.</p>
          </div>
          <div className="text-xs text-white/50">©2026 NuxFleet. All rights reserved.</div>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
          <div className="mb-6 flex justify-center lg:hidden"><Logo /></div>
          {branding?.has_logo && (
            <div className="mb-5 flex items-center gap-3 rounded-xl border bg-muted/30 p-3" data-testid="login-company-brand">
              <img src={`${API}/tenant/logo/${branding.tenant_id}?v=${branding.logo_version || 0}`} alt={branding.company_name} className="h-10 w-10 shrink-0 rounded-lg border bg-white object-contain" data-testid="login-company-logo" />
              <div className="min-w-0"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">Signing in to</div><div className="truncate font-display font-semibold" data-testid="login-company-name">{branding.company_name}</div></div>
            </div>
          )}
          <h1 className="font-display text-2xl font-semibold">Welcome back</h1>
          <p className="mb-6 text-sm text-muted-foreground">Sign in to your compliance portal</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" autoComplete="username" inputMode="email" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button type="button" onClick={() => setForgotOpen(true)} className="text-xs font-medium text-[hsl(var(--primary))] hover:underline" data-testid="login-forgot-link">Forgot password?</button>
              </div>
              <div className="relative mt-1">
                <Input id="password" data-testid="login-password-input" type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" autoComplete="current-password" />
                <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? 'Hide password' : 'Show password'} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="login-password-toggle">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={remember} onCheckedChange={setRemember} data-testid="login-remember-switch" />
              <span className="text-sm text-muted-foreground">Remember my email on this device</span>
            </div>
            <Button type="submit" disabled={loading} className="h-11 w-full bg-[hsl(var(--gold))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--gold))]/90" data-testid="login-submit-button">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Continue
            </Button>
          </form>

          <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />OR<span className="h-px flex-1 bg-border" /></div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Button variant="outline" className="h-11 w-full" onClick={google} data-testid="login-google-button">
              <img alt="g" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="mr-2 h-4 w-4" /> Google
            </Button>
            <Button variant="outline" className="h-11 w-full" onClick={microsoft} data-testid="login-microsoft-button">
              <svg className="mr-2 h-4 w-4" viewBox="0 0 23 23" aria-hidden="true"><rect x="1" y="1" width="10" height="10" fill="#F25022" /><rect x="12" y="1" width="10" height="10" fill="#7FBA00" /><rect x="1" y="12" width="10" height="10" fill="#00A4EF" /><rect x="12" y="12" width="10" height="10" fill="#FFB900" /></svg> Microsoft
            </Button>
          </div>

          <div className="mt-6 rounded-lg border bg-muted/40 p-3 text-sm">
            <div className="flex items-center gap-2 font-medium"><KeyRound className="h-4 w-4 text-[hsl(var(--primary))]" />Have an invitation code?</div>
            <p className="mt-1 text-xs text-muted-foreground">NuxFleet is invitation-only. <button type="button" onClick={() => navigate('/signup')} className="font-medium text-[hsl(var(--primary))] hover:underline" data-testid="login-signup-link">Redeem your code</button> to create your account.</p>
          </div>

          {/* Collapsible demo quick-fill helper — hidden on production origins */}
          {showQuickFill && (
          <div className="mt-4">
            <button type="button" onClick={() => setQuickOpen((v) => !v)} className="flex w-full items-center justify-center gap-1.5 rounded-full border bg-white px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted" data-testid="quick-fill-toggle">
              <Zap className="h-3.5 w-3.5 text-[hsl(var(--gold))]" />Quick Fill: demo credentials <ChevronDown className={`h-3.5 w-3.5 transition-transform ${quickOpen ? 'rotate-180' : ''}`} />
            </button>
            {quickOpen && (
              <div className="mt-2 flex flex-wrap justify-center gap-1.5" data-testid="quick-fill-panel">
                {DEMO_ACCOUNTS.map((a) => (
                  <button key={a.email} type="button" onClick={() => quickFill(a)} className="rounded-full border bg-muted/40 px-2.5 py-1 text-[11px] font-medium hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]" data-testid={`quick-fill-${a.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}>{a.label}</button>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      </div>
      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} initialEmail={email} />
    </div>
  );
}

function ForgotPasswordDialog({ open, onOpenChange, initialEmail }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  React.useEffect(() => { if (open) { setEmail(initialEmail || ''); setSent(false); } }, [open, initialEmail]);
  const submit = async () => {
    if (!email) { toast.error('Enter your email'); return; }
    setLoading(true);
    try { await client.post('/auth/forgot-password', { email, origin: window.location.origin }); setSent(true); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Something went wrong'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="forgot-password-dialog">
        <DialogHeader><DialogTitle>Reset your password</DialogTitle></DialogHeader>
        {sent ? (
          <p className="text-sm text-muted-foreground" data-testid="forgot-password-sent">If an account exists for <b>{email}</b>, we've emailed a secure reset link. It expires in 60 minutes. Check your inbox (and spam).</p>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Enter your account email and we'll send you a link to set a new password.</p>
            <div><Label>Email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" data-testid="forgot-password-email" /></div>
          </div>
        )}
        <DialogFooter>
          {sent ? <Button onClick={() => onOpenChange(false)} data-testid="forgot-password-done">Done</Button> : (<>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={submit} disabled={loading} data-testid="forgot-password-submit">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send reset link</Button>
          </>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
